import { useState, useEffect, useCallback } from "react";
import io from "socket.io-client";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

export const MYM_GAME_LABELS = {
  jukebox: "Live Music Juke Box",
  openmic: "Open Mic",
  bingo: "Live Music Bingo",
  buzzer: "Beat the Buzzer",
  flash_bingo: "Flash Bingo",
  insta_quiz: "Insta Quiz",
  higher_or_lower: "Higher or Lower",
  spinning_wheel: "Spin the Wheel",
  beerillioner: "Who wants to beer Millionaire",
};

export const MYM_TITLE_CATEGORIES = [
  {
    id: "mym",
    label: "MYM",
    ids: ["bingo", "insta_quiz", "higher_or_lower", "spinning_wheel", "beerillioner"],
  },
  {
    id: "jukebox",
    label: "Jukebox",
    ids: ["openmic", "jukebox"],
  },
  {
    id: "coming_soon",
    label: "Coming soon",
    ids: ["buzzer", "flash_bingo"],
  },
];

const normalizeConfig = (rawConfig) => {
  const knownIds = Object.keys(MYM_GAME_LABELS);
  return knownIds.map((id) => {
    const existing = rawConfig?.find((item) => item.id === id);
    return (
      existing || {
        id,
        hidden: false,
        enabled: true,
        splashTitle: "",
        splashDescription: "",
        forceModal: false,
      }
    );
  });
};

export const useMymTitlesConfig = () => {
  const [mymConfig, setMymConfig] = useState([]);
  const [mymLoading, setMymLoading] = useState(true);
  const [mymSaving, setMymSaving] = useState(false);
  const [forceWarningOpen, setForceWarningOpen] = useState(false);
  const [forceWarningMessage, setForceWarningMessage] = useState("");
  const [forceExclusiveOpen, setForceExclusiveOpen] = useState(false);
  const [pendingForceId, setPendingForceId] = useState(null);
  const [splashEditOpen, setSplashEditOpen] = useState(false);
  const [splashEditItem, setSplashEditItem] = useState(null);
  const [splashTitle, setSplashTitle] = useState("");
  const [splashDescription, setSplashDescription] = useState("");

  const fetchMymConfig = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setMymLoading(true);
      const res = await fetch(`${API_BASE}/api/settings/public/mym-titles-config`);
      const data = await res.json();
      if (data.config && Array.isArray(data.config)) {
        setMymConfig(normalizeConfig(data.config));
      }
    } catch (err) {
      console.error("Failed to fetch MYM config:", err);
    } finally {
      setMymLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMymConfig();
  }, [fetchMymConfig]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    const refresh = () => fetchMymConfig({ silent: true });
    socket.on("mym.force_open", refresh);
    socket.on("mym.force_clear", refresh);
    return () => {
      socket.disconnect();
    };
  }, [fetchMymConfig]);

  const updateMymItem = (id, updates) => {
    setMymConfig((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  const saveMymConfig = async (configToSave) => {
    const raw = configToSave ?? mymConfig;
    const payload = raw.map((item) => ({
      ...item,
      splashEnabled: !!item.enabled,
      forceModal: !!item.forceModal,
    }));
    setMymSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/mym-titles-config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
        },
        body: JSON.stringify({ config: payload }),
      });
      if (res.ok) {
        if (configToSave) setMymConfig(payload);
      } else {
        const data = await res.json().catch(() => ({}));
        console.error(data.message || "Failed to save MYM config");
      }
    } catch (err) {
      console.error("Failed to save MYM config:", err);
    } finally {
      setMymSaving(false);
    }
  };

  const handleMymToggle = (id, field, value) => {
    const currentItem = mymConfig.find((i) => i.id === id);
    if (!currentItem) return;

    if (currentItem.forceModal && (field === "hidden" || field === "enabled")) {
      setForceWarningMessage(
        "Force modal is ON for this card. You cannot hide it or turn off On Air. Please turn off Force modal first."
      );
      setForceWarningOpen(true);
      return;
    }

    if (field === "forceModal" && value) {
      if (currentItem.hidden || !currentItem.enabled) {
        setForceWarningMessage(
          "To force this card, both 'Show tile' and 'On Air' must be ON. Please enable them first."
        );
        setForceWarningOpen(true);
        return;
      }

      const alreadyForced = mymConfig.find(
        (item) => item.id !== id && item.forceModal
      );
      if (alreadyForced) {
        setPendingForceId(id);
        setForceExclusiveOpen(true);
        return;
      }
    }

    updateMymItem(id, { [field]: value });
    const next = mymConfig.map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    );
    saveMymConfig(next);
  };

  const handleGoLiveStop = (id) => {
    const currentItem = mymConfig.find((item) => item.id === id);
    if (!currentItem) return;

    const isLive = !currentItem.hidden && !!currentItem.enabled;
    if (isLive && currentItem.forceModal) {
      setForceWarningMessage(
        "Force modal is ON for this card. You cannot hide it or turn off On Air. Please turn off Force modal first."
      );
      setForceWarningOpen(true);
      return;
    }

    const next = mymConfig.map((item) =>
      item.id === id
        ? { ...item, hidden: isLive, enabled: !isLive }
        : item
    );
    setMymConfig(next);
    saveMymConfig(next);
  };

  const handleCategoryBulk = (ids, field, turnOn) => {
    const idSet = new Set(ids);
    const targets = mymConfig.filter((item) => idSet.has(item.id));
    if (!targets.length) return;

    if (!turnOn && targets.some((item) => item.forceModal)) {
      setForceWarningMessage(
        "Force modal is ON for a card in this category. You cannot hide it or turn off On Air. Please turn off Force modal first."
      );
      setForceWarningOpen(true);
      return;
    }

    const next = mymConfig.map((item) => {
      if (!idSet.has(item.id)) return item;
      if (field === "hidden") return { ...item, hidden: !turnOn };
      return { ...item, enabled: !!turnOn };
    });
    setMymConfig(next);
    saveMymConfig(next);
  };

  const confirmForceExclusive = () => {
    if (!pendingForceId) {
      setForceExclusiveOpen(false);
      return;
    }
    const next = mymConfig.map((item) => {
      if (item.id === pendingForceId) {
        return { ...item, forceModal: true };
      }
      if (item.forceModal) {
        return { ...item, forceModal: false };
      }
      return item;
    });
    setMymConfig(next);
    saveMymConfig(next);
    setPendingForceId(null);
    setForceExclusiveOpen(false);
  };

  const cancelForceExclusive = () => {
    setPendingForceId(null);
    setForceExclusiveOpen(false);
  };

  const openSplashEdit = (item) => {
    setSplashEditItem(item);
    setSplashTitle(item.splashTitle || "");
    setSplashDescription(item.splashDescription || "");
    setSplashEditOpen(true);
  };

  const closeSplashEdit = () => {
    setSplashEditOpen(false);
    setSplashEditItem(null);
  };

  const handleSplashEditSave = () => {
    if (!splashEditItem) return;
    const next = mymConfig.map((item) =>
      item.id === splashEditItem.id
        ? { ...item, splashTitle, splashDescription }
        : item
    );
    setMymConfig(next);
    saveMymConfig(next);
    closeSplashEdit();
  };

  return {
    mymConfig,
    mymLoading,
    mymSaving,
    forceWarningOpen,
    forceWarningMessage,
    forceExclusiveOpen,
    splashEditOpen,
    splashEditItem,
    splashTitle,
    splashDescription,
    setForceWarningOpen,
    setSplashTitle,
    setSplashDescription,
    handleMymToggle,
    handleGoLiveStop,
    handleCategoryBulk,
    confirmForceExclusive,
    cancelForceExclusive,
    openSplashEdit,
    closeSplashEdit,
    handleSplashEditSave,
  };
};
